import React from 'react';
import styled from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import {
  PageWrapper,
  HeroSection,
  Container,
  PageTitle,
  PageSubtitle,
  PremiumCard,
  Section
} from '../styles/PremiumStyles';
import { theme } from '../styles/theme';

const QuestionSection = styled(Section)`
  padding: 80px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #f8f9fc 100%);
`;

const SeniorFriendlyCard = styled(PremiumCard)`
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px;
  background: white;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 30px 20px;
  }
`;

const QuestionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 40px;
  line-height: 1.3;
  text-align: center;
  
  span {
    background: linear-gradient(135deg, #FFD700, #FFA500);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 36px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 28px;
  }
`;

const QuestionText = styled.div`
  font-size: 24px;
  line-height: 1.8;
  color: ${theme.colors.text.primary};
  margin-bottom: 50px;
  letter-spacing: 0.3px;
  
  p {
    margin-bottom: 30px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 20px;
    line-height: 1.7;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 18px;
    line-height: 1.6;
  }
`;

const AnswerSection = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 20px;
  padding: 50px;
  margin-top: 50px;
  border: 2px solid #0ea5e9;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 30px 20px;
  }
`;

const AnswerTitle = styled.h3`
  font-size: 36px;
  font-weight: 700;
  color: #0284c7;
  margin-bottom: 30px;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 28px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 24px;
  }
`;

const AnswerText = styled.div`
  font-size: 22px;
  line-height: 1.8;
  color: ${theme.colors.text.secondary};
  
  p {
    margin-bottom: 25px;
  }
  
  strong {
    color: ${theme.colors.text.primary};
    font-weight: 600;
  }
  
  ul {
    margin: 30px 0;
    padding-left: 30px;
  }
  
  li {
    margin-bottom: 20px;
    font-size: 20px;
    line-height: 1.6;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 19px;
    
    li {
      font-size: 18px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 17px;
    
    li {
      font-size: 16px;
    }
  }
`;

const KeyPointsBox = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px;
  margin: 40px 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border-left: 5px solid ${theme.colors.primary};
  
  h4 {
    font-size: 28px;
    font-weight: 700;
    color: ${theme.colors.text.primary};
    margin-bottom: 25px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 30px 20px;
    
    h4 {
      font-size: 22px;
    }
  }
`;

const QuintessentialQuestion: React.FC = () => {
  return (
    <SharedLayoutUltimate>
      <PageWrapper>
        <HeroSection>
          <Container>
            <PageTitle style={{ color: 'white' }}>
              The <span className="gold">Quintessential Question</span>
            </PageTitle>
            <PageSubtitle style={{ color: 'white' }}>
              Understanding the Critical Factors in Annuity Illustrations
            </PageSubtitle>
          </Container>
        </HeroSection>
        
        <QuestionSection>
          <Container>
            <SeniorFriendlyCard>
              <QuestionTitle>
                The Question Every Annuity Buyer <span>Should Ask</span>
              </QuestionTitle>
              
              <QuestionText>
                <p>
                  <strong>Here's the quintessential question you should ask when evaluating any annuity illustration:</strong>
                </p>
                
                <KeyPointsBox>
                  <p style={{ fontSize: '26px', lineHeight: '1.8', fontStyle: 'italic', color: theme.colors.text.primary }}>
                    "What assumptions go into this annuity illustration, and how might they affect the expectations I have compared to actual results? Are the returns shown based on real historical index performance, or on backfilled simulations created after the index was launched? When reviewing backtesting results, how can I know whether the process avoids common distortions—such as cherry-picking dates, using backfilled data, or overlooking changes in rates over time—and instead relies on transparent, consistent methods that give me a fair picture of long-term performance?"
                  </p>
                </KeyPointsBox>
              </QuestionText>
              
              <AnswerSection>
                <AnswerTitle>GUIDANCE</AnswerTitle>
                
                <AnswerText>
                  <KeyPointsBox>
                    <h4>A1. Understanding Illustration Assumptions</h4>
                    <p>
                      Annuity illustrations are built on assumptions about index performance, crediting rates (caps, participation, spreads), and how the carrier might adjust those rates in the future. These are not guarantees. Actual results can differ if the company lowers rates, if the chosen index behaves differently than shown, or if the illustration uses simulated (backfilled) data from before the index existed. That's why it's important to understand what's guaranteed versus what's hypothetical, and to compare illustrations with tools that rely on real historical data.
                    </p>
                  </KeyPointsBox>
                  
                  <KeyPointsBox>
                    <h4>A2. The Backfilling Problem</h4>
                    <p>
                      Many annuity illustrations rely heavily on backfilled simulations — data that was modeled after the index was created. This can create an overly positive impression because the index never actually went through those periods in real time. By contrast, testing based strictly on real index history shows how the product would have behaved under actual market conditions. That distinction is crucial when evaluating whether an annuity's performance assumptions are realistic.
                    </p>
                  </KeyPointsBox>
                  
                  <KeyPointsBox>
                    <h4>A3. How to Identify Reliable Backtesting</h4>
                    <p>
                      The strongest backtesting approaches deliberately work to eliminate distortions. For example, Paragon's AnnuityAnswers platform uses:
                    </p>
                    <ul style={{ marginTop: '20px' }}>
                      <li><strong>Real daily market data only</strong> — no backfilled, hypothetical index history.</li>
                      <li><strong>Permutation testing</strong> — purchasing contracts across every possible start date, instead of cherry-picking.</li>
                      <li><strong>Rate-change recognition</strong> — factoring in how caps, spreads, and participation rates actually moved over time.</li>
                      <li><strong>Transparency</strong> — results are shown plainly, not just the best-performing period.</li>
                    </ul>
                    <p style={{ marginTop: '20px' }}>
                      While there isn't formal third-party accreditation of this process, the methodology itself directly addresses the main sources of misleading results in traditional illustrations. Combined with our decades-long industry track record and public presence (CNBC, Tom Hegna's program), consumers can reasonably trust the intent and rigor behind the data.
                    </p>
                  </KeyPointsBox>
                  
                  <p style={{ marginTop: '40px', fontSize: '24px', textAlign: 'center', fontWeight: '600' }}>
                    At Annuity Answers, we use only transparent, consistent backtesting methods with real historical data to give you the clearest picture of potential performance.
                  </p>
                </AnswerText>
              </AnswerSection>
            </SeniorFriendlyCard>
          </Container>
        </QuestionSection>
      </PageWrapper>
    </SharedLayoutUltimate>
  );
};

export default QuintessentialQuestion;